import { z } from 'zod';

import { CoverImage, INNER_DATA_KEY, STORY_ASSET } from './envelope.js';

/**
 * The learning-asset READ contract — what the API serves, as against what an
 * author must write.
 *
 * `./envelope.js` is the write contract: it pins the constants, the closed
 * template union and the minimum lengths a document must satisfy before it can
 * be published, and the envelope rules tighten it further. A read runs no
 * validation at all — the corpus predates the validator, and refusing to
 * describe an asset is not a read's job — so this contract carries the same
 * shape with the authoring constraints dropped and everything the CMS can
 * leave empty typed nullable.
 *
 * Two deliberate additions to the write envelope, facts about what the server
 * sends rather than opinions about what content should be: `draftIsPublished`
 * rides alongside the document, so a reader knows whether the draft it just
 * received is the live one without a second call, and `revision` names the
 * envelope as it was read, so a write can send it back as `expectedRevision`
 * and be refused if the asset has changed since. An environment that does not
 * serve `revision` yet checks nothing.
 *
 * Phoenix's `/api/v2/learning-assets` responses and hydra's reader both compile
 * against these types.
 */

/** An authored block carried verbatim: present-or-null on a read, shaped on write. */
const AuthoredBlock = z.record(z.string(), z.unknown());

export const ReadEnvelopeFields = z.looseObject({
  coinValue: z.looseObject({ coins: z.number() }).nullable(),
  considerationAreaSlugs: z.array(z.string()).nullable(),
  coverImage: CoverImage,
  duration: z.number().nullable(),
  indexing: AuthoredBlock.nullable(),
  internalName: z.string().nullable(),
  meta: AuthoredBlock.nullable(),
  name: z.string(),
  order: z.number().nullable(),
  publishedAt: z.string().nullable(),
  slug: z.string(),
  structuredLearningOutcome: AuthoredBlock.nullable(),
  subtitle: z.string().nullable(),
  // A free string, not the closed `TemplateName` union: narrowing it is the
  // write path's job, and `isTemplateName` is there for a reader that needs it.
  templateName: z.string().nullable(),
  type: z.literal(STORY_ASSET),
});
export type ReadEnvelopeFields = z.infer<typeof ReadEnvelopeFields>;

export const ReadContentItem = z.looseObject({
  id: z.string(),
  type: z.literal(STORY_ASSET),
  [INNER_DATA_KEY]: z.unknown(),
});
export type ReadContentItem = z.infer<typeof ReadContentItem>;

export const ReadContentEntry = z.looseObject({
  id: z.string(),
  learningAssetContentItems: z.array(ReadContentItem),
  type: z.literal(STORY_ASSET),
});
export type ReadContentEntry = z.infer<typeof ReadContentEntry>;

export const ReadEnvelope = z.looseObject({
  // Empty until the asset has draft content, so never the write contract's
  // exactly-one entry.
  content: z.array(ReadContentEntry),
  draftIsPublished: z.boolean(),
  fields: ReadEnvelopeFields,
  revision: z.string().optional(),
  schemaVersion: z.literal(1),
});
export type ReadEnvelope = z.infer<typeof ReadEnvelope>;

export const LearningAssetPageMeta = z.looseObject({
  currentPage: z.number(),
  lastPage: z.number(),
  perPage: z.number(),
  total: z.number(),
});
export type LearningAssetPageMeta = z.infer<typeof LearningAssetPageMeta>;

export const LearningAssetListResponse = z.looseObject({
  data: z.array(ReadEnvelope),
  meta: LearningAssetPageMeta,
});
export type LearningAssetListResponse = z.infer<typeof LearningAssetListResponse>;

export const LearningAssetResponse = z.looseObject({
  data: ReadEnvelope,
});
export type LearningAssetResponse = z.infer<typeof LearningAssetResponse>;
